<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CortexModelRoutesSettings from './CortexModelRoutesSettings.vue';
import CortexModelProfilesSettings from './CortexModelProfilesSettings.vue';
import CortexProviderConnectionsSettings from './CortexProviderConnectionsSettings.vue';
import CortexModelBehaviorSettings from './CortexModelBehaviorSettings.vue';
import CortexModelSetupWizard from './CortexModelSetupWizard.vue';
import CortexUrlSettings from './CortexUrlSettings.vue';

const { t } = useI18n();
const activeTab = ref('connections');
const showSetupWizard = ref(false);
const setupConnection = ref(null);
const refreshKey = ref(0);

const tabs = computed(() => [
  { key: 'connections', label: t('CORTEX_MODELS.TABS.CONNECTIONS') },
  { key: 'profiles', label: t('CORTEX_MODELS.TABS.MODEL_PROFILES') },
  { key: 'routes', label: t('CORTEX_MODELS.TABS.MODEL_ROUTES') },
  { key: 'firecrawl', label: t('CORTEX_MODELS.TABS.WEB_EXTRACTION') },
  { key: 'behavior', label: t('CORTEX_MODELS.TABS.BEHAVIOR') },
]);

const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);

const startSetupWizard = () => {
  setupConnection.value = null;
  showSetupWizard.value = true;
};

const editSetupWizard = connection => {
  setupConnection.value = connection;
  showSetupWizard.value = true;
};

const finishSetupWizard = () => {
  showSetupWizard.value = false;
  setupConnection.value = null;
  activeTab.value = 'connections';
  refreshKey.value += 1;
};

const closeSetupWizard = () => {
  showSetupWizard.value = false;
  setupConnection.value = null;
};

const configureRoutes = () => {
  activeTab.value = 'routes';
  refreshKey.value += 1;
};
</script>

<template>
  <div class="flex flex-col min-w-0 gap-6">
    <div>
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('CORTEX_MODELS.TITLE') }}
      </h3>
      <p class="mt-2 text-body-main text-n-slate-11">
        {{ t('CORTEX_MODELS.DESCRIPTION') }}
      </p>
    </div>

    <CortexModelSetupWizard
      v-if="showSetupWizard"
      :connection="setupConnection"
      @cancel="closeSetupWizard"
      @completed="finishSetupWizard"
    />

    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      @tab-changed="activeTab = $event.key"
    />

    <CortexModelRoutesSettings
      v-if="activeTab === 'routes'"
      :key="`routes-${refreshKey}`"
    />
    <CortexModelProfilesSettings
      v-else-if="activeTab === 'profiles'"
      :key="`profiles-${refreshKey}`"
    />
    <CortexProviderConnectionsSettings
      v-else-if="activeTab === 'connections'"
      :key="`connections-${refreshKey}`"
      @start-setup="startSetupWizard"
      @edit-setup="editSetupWizard"
      @configure-routes="configureRoutes"
    />
    <CortexUrlSettings v-else-if="activeTab === 'firecrawl'" firecrawl-only />
    <CortexModelBehaviorSettings v-else />
  </div>
</template>
